import { useMemo } from 'react';
import type { VisibleFieldModel } from 'shared/domain/visibleField/visibleFieldModel';
import { toVisibleFieldModelMap } from 'shared/domain/visibleField/visibleFieldsMap';
import type { HashMap } from 'shared/types/commonView';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { useProcesses } from 'store/process';
import { getVisibleFieldStoreState, useVisibleFieldStore } from '../store';
import { groupVisibleFieldsByProcess, visibleFieldsOfProcess } from '../model';
import type { EditVisibleFieldsAction, VisibleFieldActions } from '../types';

export function useVisibleFieldsInitializer(): void {
  useEntityCollectionInitializer(useVisibleFieldStore.use.actions());
}

/** Visible fields of each process of the selected project. */
export function useVisibleFieldsByProcess(): HashMap<VisibleFieldModel[]> {
  const { items } = useVisibleFieldStore.use.data();
  const { processes } = useProcesses();
  return useMemo(() => groupVisibleFieldsByProcess(items, processes), [items, processes]);
}

/** Per process, whether each field is shown (fields that cannot be hidden included). */
export function useVisibleFieldsMap(): HashMap<HashMap<boolean>> {
  const visibleFieldsByProcess = useVisibleFieldsByProcess();
  return useMemo(() => toVisibleFieldModelMap(visibleFieldsByProcess), [visibleFieldsByProcess]);
}

export function useVisibleFieldsLoading(): boolean {
  return useVisibleFieldStore.use.data().loading;
}

export function useVisibleFieldsReload(): VisibleFieldActions['reload'] {
  return useVisibleFieldStore.use.actions().reload;
}

export function useEditVisibleFields(): EditVisibleFieldsAction {
  const edit = useVisibleFieldStore.use.asyncActions().edit;
  const state = useVisibleFieldStore.use._actionStates().edit;
  return useMemo(() => ({ execute: edit, ...state }), [edit, state]);
}

/** For event handlers: the fields read at the moment of the event. */
export function getVisibleFieldsOfProcess(processId: string): VisibleFieldModel[] {
  return visibleFieldsOfProcess(getVisibleFieldStoreState().data.items, processId);
}
